import React, { useCallback, useMemo } from 'react'
import { Form, Input, Button, Typography, App } from 'antd'
import { UserOutlined, LockOutlined, EyeTwoTone, EyeInvisibleOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '@/libs/context/auth'
import AuthService from '@/libs/services/auth'
import StorageService from '@/libs/services/storage'

const { Title, Text } = Typography

const StyledForm = styled(Form)`
  width: 100%;
`

const SubmitButton = styled(Button)`
  height: 45px;
  background-color: #008484;
  border-color: #008484;
  &:hover {
    background-color: #009999;
    border-color: #009999;
  }
`

const FormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
`

const StyledLink = styled(Link)`
  color: #00838c;
  &:hover {
    color: #009999;
  }
`

const FooterText = styled.div`
  text-align: center;
  margin-top: 24px;

  a {
    color: #00838c;
    &:hover {
      color: #009999;
    }
  }
`

const LoginView = () => {
  const [form] = Form.useForm()
  const { login } = useAuth()
  const navigate = useNavigate()
  const { notification } = App.useApp()

  const onFinish = useCallback(
    async values => {
      try {
        const response = await AuthService.submitLogin({
          email: values.email,
          password: values.password,
        })

        const { access, refresh } = response.data

        StorageService.updateUserToken(access)
        StorageService.updateRefreshToken(refresh)

        login({
          email: values.email,
          token: access,
          refresh: refresh,
        })

        notification.success({
          message: 'Success',
          description: 'Login successful! Redirecting...',
          placement: 'topRight',
          duration: 3,
        })

        navigate('/dashboard')
      } catch {
        notification.error({
          message: 'Error',
          description: 'Invalid email or password',
          placement: 'topRight',
          duration: 3,
        })
        form.setFields([
          {
            name: 'password',
            errors: ['Invalid email or password'],
          },
        ])
      }
    },
    [form, login, navigate, notification]
  )

  const formRules = useMemo(
    () => ({
      email: [
        { required: true, message: 'Please input your email!' },
        { type: 'email', message: 'Please enter a valid email address!' },
      ],
      password: [{ required: true, message: 'Please input your password!' }],
    }),
    []
  )

  return (
    <>
      <StyledForm form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
        <Form.Item
          name="email"
          rules={formRules.email}
          validateTrigger="onBlur"
          label={
            <>
              <span style={{ color: '#4d4d4d' }}>*</span> Email
            </>
          }
        >
          <Input placeholder="admin" size="large" />
        </Form.Item>
        <Form.Item
          name="password"
          rules={formRules.password}
          validateTrigger="onBlur"
          label={
            <>
              <span style={{ color: '#4d4d4d' }}>*</span> Password
            </>
          }
        >
          <Input.Password
            placeholder="••••••••••••"
            size="large"
            iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <FormFooter>
          <StyledLink to="/auth/forgot-password">Forgot password</StyledLink>
        </FormFooter>

        <Form.Item style={{ marginTop: 24 }}>
          <SubmitButton type="primary" htmlType="submit" block>
            Login
          </SubmitButton>
        </Form.Item>
      </StyledForm>

      <FooterText>
        Don't have an account yet? <StyledLink to="/auth/register">Register</StyledLink>
      </FooterText>
    </>
  )
}

export default LoginView
